import { ActionIcon, Button, Checkbox, Group, Stack, Text } from '@mantine/core';
import { IconPencil, IconPlus } from '@tabler/icons-react';
import { type TranslateFunction, tKeys, useTranslate } from 'locales/index';
import { isTruthy } from 'remeda';

export type ReminderAddress = {
  id?: string;
  additional_line_1?: string;
  additional_line_2?: string;
  street_name?: string;
  street_number?: string;
  street_box?: string;
  zip_code?: string;
  city?: string;
  country_code?: string;
  selected?: boolean;
};

type ReminderPostalAddressesProps = {
  addresses: Array<ReminderAddress>;
  onAdd: () => void;
  onEdit: (index: number) => void;
  onToggle: (index: number) => void;
};

const formatAddress = (address: ReminderAddress, t: TranslateFunction) => {
  const box = isTruthy(address.street_box)
    ? `${t(tKeys.FORM.ADDRESS.BOX)} ${address.street_box}`
    : '';
  const line = [address.street_name, address.street_number, box].filter(isTruthy).join(' ');
  const locality = [address.zip_code, address.city].filter(isTruthy).join(' ');
  return [line, locality].filter(isTruthy).join(' · ');
};

export const ReminderPostalAddresses = ({
  addresses,
  onAdd,
  onEdit,
  onToggle,
}: ReminderPostalAddressesProps) => {
  const t = useTranslate();

  return (
    <Stack gap="xs">
      {addresses.map((address, index) => (
        <Group key={address.id ?? formatAddress(address, t)} justify="space-between" wrap="nowrap">
          <Group gap="xs" wrap="nowrap">
            <Checkbox
              checked={address.selected}
              onChange={() => onToggle(index)}
              aria-label={formatAddress(address, t)}
            />
            <Text size="sm">{formatAddress(address, t)}</Text>
          </Group>
          <ActionIcon variant="subtle" aria-label={t(tKeys.EDIT)} onClick={() => onEdit(index)}>
            <IconPencil size={16} />
          </ActionIcon>
        </Group>
      ))}
      <Button
        variant="subtle"
        size="compact-sm"
        leftSection={<IconPlus size={16} />}
        onClick={onAdd}
        w="fit-content"
      >
        {t(tKeys.FORM.ADDRESS.ADD)}
      </Button>
    </Stack>
  );
};
